import React, { useEffect, useState, useMemo } from "react";
import axios from "../context/axiosConfig";
import requireAuth from "../context/requireAuth";
import MaterialReactTable from 'material-react-table';
import { Box, Button, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import TimelineIcon from '@mui/icons-material/Timeline';
import { ExportToCsv } from 'export-to-csv';
import { useLocation, useNavigate } from "react-router-dom";

function PlantSelect() {
  const location = useLocation();
  const navigate = useNavigate();
  const org_id = localStorage.getItem("organization");

  const [plants, setPlants] = useState([]);
  const [ultimatePlants, setUltimatePlants] = useState([]);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [selectedPlotNo, setSelectedPlotNo] = useState('');
  const [showOnlyDestroyed, setShowOnlyDestroyed] = useState(false);
  const [enhancedView] = useState(true); // Default to true
  const [isAdmin, setIsAdmin] = useState(false);

  const [maintenanceTypes, setMaintenanceTypes] = useState({
    phenotyping: false,
    measurement: false,
    harvest: false,
    postharvest: false,
    irrigation: false,
    scouting: false,
    application: false,
    manipulation: false,
    status: false
  });


  useEffect(() => {
          axios.get(`${org_id}/checkuser`, {withCredentials: true})
              .then(res => {
              if (res.data.role === "admin") {
                  setIsAdmin(true);
              }
              })
              .catch(err => console.log(err));
      }, [])

  useEffect(() => {
    axios.get(`orgs/${org_id}/plants/`, { withCredentials: true })
      .then(res => {
        setPlants(res.data)
        console.log(res.data);
      })
      .catch(err => console.log(err));
  }, []);

  useEffect(() => {
    axios.get(`orgs/${org_id}/plants/ultimate`, { withCredentials: true })
      .then(res => setUltimatePlants(res.data))
      .catch(err => console.log(err));
  }, []);

  const [countAll, setCountAll] = useState([]);
  useEffect(() => {
    axios.get(`orgs/${org_id}/plants/count/all`, { withCredentials: true })
      .then(res => setCountAll(res.data))
      .catch(err => console.log(err));
  }, []);

  const [countActive, setCountActive] = useState([]);
  useEffect(() => {
    axios.get(`orgs/${org_id}/plants/count/active`, { withCredentials: true })
      .then(res => setCountActive(res.data))
      .catch(err => console.log(err));
  }, []);

  const [countRemoved, setCountRemoved] = useState([]);
  useEffect(() => {
    axios.get(`orgs/${org_id}/plants/count/removed`, { withCredentials: true })
      .then(res => setCountRemoved(res.data))
      .catch(err => console.log(err));
  }, []);

  const [varieties, setVarieties] = useState([]);
  useEffect(() => {
    axios.get(`orgs/${org_id}/plants/varieties`, { withCredentials: true })
      .then(res => setVarieties(res.data))
      .catch(err => console.log(err));
  }, []);

  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    axios.get(`orgs/${org_id}/rooms/`, { withCredentials: true })
      .then(res => setRooms(res.data))
      .catch(err => console.log(err));
  }, []);

  const [plots, setPlots] = useState([]);
  useEffect(() => {
    axios.get(`orgs/${org_id}/plots/`, { withCredentials: true })
      .then(res => setPlots(res.data))
      .catch(err => console.log(err));
  }, []);

const baseColumns = useMemo(() => [
  {
    accessorFn: (row) => {
      const value = row.org_plant_no;
      return value !== null && value !== undefined ? String(value) : "N/A";
    },
    id: 'org_plant_no',
    header: 'Plant Number',
    Cell: ({ row }) => {
      const value = row.original.org_plant_no;
      return value !== null && value !== undefined ? String(value) : "N/A";
    },
    filterFn: 'equals'
  },
  {
    accessorKey: 'variety',
    header: 'Variety',
  },
  {
    accessorKey: 'sex', 
    header: 'Sex',
  },
  {
    accessorKey: 'loc_x',
    header: 'Plot Column',
  },
  {
    accessorKey: 'loc_y',
    header: 'Plot Row',
  },
  {
    accessorKey: 'org_plot_no',
    header: 'Plot ID',
    Cell: ({ row }) => {
      const plotId = row.original.plot_id;
      const plot = plots.find(p => p.plot_id === plotId);
      if (!plot) return 'No plot';
      const plotLabel = plot.plotno || `Plot ${plot.org_plot_no}`;
      return `${plot.org_plot_no}${plotLabel ? ` - ${plotLabel}` : ''}`;
    }
  },
  {
    accessorKey: 'datecreated',
    header: 'Date Created',
    Cell: ({ cell }) => {
      const date = cell.row.original.datecreated;
      return date
        ? new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        : 'NO DATE';
    },
  },
  {
    accessorKey: 'datedestroyed',
    header: 'Date Destroyed',
    Cell: ({ cell }) => {
      const date = cell.row.original.datedestroyed;
      return date
        ? new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        : 'NO DATE';
    },
  },
], [plots]); // 👈 add this


  const enhancedColumns = useMemo(() => {
    const columns = [...baseColumns];

    if (maintenanceTypes.phenotyping) {
      columns.push(
        {
          accessorKey: 'latest_phenotyping_sex',
          header: 'Latest Sex (Phenotyping)',
        },
        {
          accessorKey: 'latest_germination_speed',
          header: 'Germination Speed',
        },
        {
          accessorKey: 'latest_flower_density',
          header: 'Flower Density',
        },
        {
          accessorKey: 'latest_internode_spacing',
          header: 'Internode Spacing',
        },
        {
          accessorKey: 'latest_trichome_expression',
          header: 'Trichome Expression',
        },
        {
          accessorKey: 'latest_leaf_formations',
          header: 'Leaf Formations',
          Cell: ({ cell }) => {
            const formations = cell.getValue();
            return Array.isArray(formations) ? formations.join(', ') : formations || 'N/A';
          }
        },
        {
          accessorKey: 'latest_plant_architecture',
          header: 'Plant Architecture',
        },
        {
          accessorKey: 'latest_flower_architectures',
          header: 'Flower Architectures',
          Cell: ({ cell }) => {
            const architectures = cell.getValue();
            return Array.isArray(architectures) ? architectures.join(', ') : architectures || 'N/A';
          }
        },
        {
          accessorKey: 'latest_flower_colours',
          header: 'Flower Colours',
          Cell: ({ cell }) => {
            const colours = cell.getValue();
            return Array.isArray(colours) ? colours.join(', ') : colours || 'N/A';
          }
        },
        {
          accessorKey: 'latest_aromas',
          header: 'Aromas',
          Cell: ({ cell }) => {
            const aromas = cell.getValue();
            if (Array.isArray(aromas)) {
              return aromas.map(aroma => `${aroma.aroma_type_name} (${aroma.intensity})`).join(', ');
            }
            return 'N/A';
          }
        },
        {
          accessorKey: 'latest_phenotyping_date',
          header: 'Latest Phenotyping Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Measurement columns
    if (maintenanceTypes.measurement) {
      columns.push(
        {
          accessorKey: 'latest_height_cm',
          header: 'Height (cm)',
        },
        {
          accessorKey: 'latest_width_cm',
          header: 'Width (cm)',
        },
        {
          accessorKey: 'latest_stem_diameter_mm',
          header: 'Stem Diameter (mm)',
        },
        {
          accessorKey: 'latest_node_count',
          header: 'Node Count',
        },
        {
          accessorKey: 'latest_branch_count',
          header: 'Branch Count',
        },
        {
          accessorKey: 'latest_weight_g',
          header: 'Weight (g)',
        },
        {
          accessorKey: 'latest_measurement_date',
          header: 'Latest Measurement Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Harvest columns
    if (maintenanceTypes.harvest) {
      columns.push(
        {
          accessorKey: 'latest_plant_weight_harvest_g',
          header: 'Harvest Weight (g)',
        },
        {
          accessorKey: 'latest_defoliated_weight_g',
          header: 'Defoliated Weight (g)',
        },
        {
          accessorKey: 'latest_leaf_weight_g',
          header: 'Leaf Weight (g)',
        },
        {
          accessorKey: 'latest_infected_weight_g',
          header: 'Infected Weight (g)',
        },
        {
          accessorKey: 'latest_infected_reason',
          header: 'Infection Reason',
        },
        {
          accessorKey: 'latest_harvest_date',
          header: 'Latest Harvest Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Postharvest columns
    if (maintenanceTypes.postharvest) {
      columns.push(
        {
          accessorKey: 'latest_plant_weight_dry_g',
          header: 'Dry Plant Weight (g)',
        },
        {
          accessorKey: 'latest_stem_weight_dry_g',
          header: 'Dry Stem Weight (g)',
        },
        {
          accessorKey: 'latest_leaf_weight_dry_g',
          header: 'Dry Leaf Weight (g)',
        },
        {
          accessorKey: 'latest_untrimmed_flower_g',
          header: 'Untrimmed Flower (g)',
        },
        {
          accessorKey: 'latest_trimmed_flower_g',
          header: 'Trimmed Flower (g)',
        },
        {
          accessorKey: 'latest_trim_weight_g',
          header: 'Trim Weight (g)',
        },
        {
          accessorKey: 'latest_postharvest_date',
          header: 'Latest Postharvest Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Irrigation columns
    if (maintenanceTypes.irrigation) {
      columns.push(
        {
          accessorKey: 'latest_irrigation_type',
          header: 'Irrigation Type',
        },
        {
          accessorKey: 'latest_ph',
          header: 'pH',
        },
        {
          accessorKey: 'latest_ec',
          header: 'EC',
        },
        {
          accessorKey: 'latest_water_temp',
          header: 'Water Temp',
        },
        {
          accessorKey: 'latest_nutrient_recipe',
          header: 'Nutrient Recipe',
        },
        {
          accessorKey: 'latest_irrigation_date',
          header: 'Latest Irrigation Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Scouting columns
    if (maintenanceTypes.scouting) {
      columns.push(
        {
          accessorKey: 'latest_scouting_type',
          header: 'Scouting Type',
        },
        {
          accessorKey: 'latest_pest_type',
          header: 'Pest Type',
        },
        {
          accessorKey: 'latest_disease_type',
          header: 'Disease Type',
        },
        {
          accessorKey: 'latest_severity',
          header: 'Severity',
        },
        {
          accessorKey: 'latest_alarm_level',
          header: 'Alarm Level',
        },
        {
          accessorKey: 'latest_scouting_date',
          header: 'Latest Scouting Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Application columns
    if (maintenanceTypes.application) {
      columns.push(
        {
          accessorKey: 'latest_application_product',
          header: 'Application Product',
        },
        {
          accessorKey: 'latest_application_type',
          header: 'Application Type',
        },
        {
          accessorKey: 'latest_application_mode',
          header: 'Application Mode',
        },
        {
          accessorKey: 'latest_application_date',
          header: 'Latest Application Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Manipulation columns
    if (maintenanceTypes.manipulation) {
      columns.push(
        {
          accessorKey: 'latest_manipulation_techniques',
          header: 'Manipulation Techniques',
          Cell: ({ cell }) => {
            const techniques = cell.getValue();
            return Array.isArray(techniques) ? techniques.join(', ') : techniques || 'N/A';
          }
        },
        {
          accessorKey: 'latest_manipulation_date',
          header: 'Latest Manipulation Date',
          Cell: ({ cell }) => {
            const date = cell.getValue();
            return date ? new Date(date).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }) : 'N/A';
          }
        }
      );
    }

    // Status columns
    if (maintenanceTypes.status) {
      columns.push(
        {
          accessorKey: 'days_since_last_maintenance',
          header: 'Days Since Last Maintenance',
        },
        {
          accessorKey: 'total_maintenance_records',
          header: 'Total Maintenance Records',
        },
        {
          accessorKey: 'is_destroyed',
          header: 'Is Destroyed',
          Cell: ({ cell }) => cell.getValue() ? 'Yes' : 'No'
        }
      );
    }

    return columns;
  }, [baseColumns, maintenanceTypes]);

  const columns = enhancedView ? enhancedColumns : baseColumns;

  // Dynamic CSV options that update based on selected columns
  const csvOptions = useMemo(() => ({
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalSeparator: '.',
    showLabels: true,
    useBom: true,
    useKeysAsHeaders: true,
    fields: columns.map((c) => c.header),
  }), [columns]);

  const csvExporter = useMemo(() => new ExportToCsv(csvOptions), [csvOptions]);

  // Helper function to extract only the visible column data from a row
  const extractVisibleData = (row) => {
    const visibleData = {};
    columns.forEach(column => {
      const key = column.accessorKey || column.id;
      let value = row[key];
      
      // Handle special formatting for arrays and complex data
      if (column.accessorKey === 'latest_leaf_formations' || 
          column.accessorKey === 'latest_flower_architectures' || 
          column.accessorKey === 'latest_flower_colours' ||
          column.accessorKey === 'latest_manipulation_techniques') {
        value = Array.isArray(value) ? value.join(', ') : (value || 'N/A');
      } else if (column.accessorKey === 'latest_aromas') {
        if (Array.isArray(value)) {
          value = value.map(aroma => `${aroma.aroma_type_name} (${aroma.intensity})`).join(', ');
        } else {
          value = 'N/A';
        }
      } else if (column.accessorKey === 'is_destroyed') {
        value = value ? 'Yes' : 'No';
      } else if (column.accessorKey === 'plot_id') {
        value = row.org_plot_no;
      } else if (column.accessorKey === 'org_plant_no') {
        value = value !== null && value !== undefined ? String(value) : "N/A";
      } else if (column.header.includes('Date') && value) {
        // Format dates consistently for CSV
        if (column.header === 'Date Created' || column.header === 'Date Destroyed') {
          value = new Date(value).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
        } else {
          value = new Date(value).toLocaleDateString();
        }
      } else if (value === null || value === undefined) {
        value = 'N/A';
      }
      
      visibleData[column.header] = value;
    });
    return visibleData;
  };

  const handleExportRows = (rows) => {
    const visibleRowData = rows.map((row) => extractVisibleData(row.original));
    csvExporter.generateCsv(visibleRowData);
  };

  const handleExportData = () => {
    const visibleData = filteredPlants.map(extractVisibleData);
    csvExporter.generateCsv(visibleData);
  };

  const handleDeletePlants = (e, rows) => {
    const deleteObject = {
      type: "plant",
      page: location.pathname,
      objects: rows.map((row) => row.original.plant_id),
      ids: rows.map((row) => row.original.org_plant_no)
    };
    window.sessionStorage.setItem("toDelete", JSON.stringify(deleteObject));
    navigate("/destroy");
  };

  const handleMaintenanceTypeChange = (type) => {
    setMaintenanceTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const filteredPlants = useMemo(() => {
    const dataSource = enhancedView ? ultimatePlants : plants;
    let filtered = dataSource;

    if (showOnlyActive) filtered = filtered.filter(plant => !plant.datedestroyed);
    if (showOnlyDestroyed) filtered = filtered.filter(plant => plant.datedestroyed);

    if (selectedPlotNo) {
      const selectedPlot = plots.find(plot => plot.org_plot_no === Number(selectedPlotNo));
      if (selectedPlot) {
        filtered = filtered.filter(plant => {
          return plant.plot_id === selectedPlot.plot_id;
        });
      }
    }

    return filtered;
  }, [plants, ultimatePlants, enhancedView, showOnlyActive, showOnlyDestroyed, selectedPlotNo, plots]);

  return (
    <div className="bodyContent">
      <label htmlFor="plants" className="subheader">Select Plants</label>

      <div style={{ marginTop: '1rem', marginBottom: '1rem', paddingLeft: "20px" }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<TimelineIcon />}
          onClick={() => navigate('/plantMovementLogs')}
          sx={{ 
            backgroundColor: '#6f42c1',
            '&:hover': { backgroundColor: '#5a2d91' }
          }}
        >
          View Plant Movement Logs
        </Button>
      </div>

      <div className="plantData">
        <div className="value">
          <div className="purpleHeader">
            <p className="valueTitle">Total Plants</p>
          </div>
          <div className="valueBox">
            <p className="valueText">{countAll.count}</p>
          </div>
        </div>
        <div className="value">
          <div className="purpleHeader">
            <p className="valueTitle">Removed Plants</p>
          </div>
          <div className="valueBox">
            <p className="valueText">{countRemoved.count}</p>
          </div>
        </div>
        <div className="value">
          <div className="purpleHeader">
            <p className="valueTitle">Active Plants</p>
          </div>
          <div className="valueBox">
            <p className="valueText">{countActive.count}</p>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1rem', paddingLeft: "20px", display: 'flex', alignItems: 'center', gap: '20px' }}>

      <FormGroup row sx={{ mb: 1 }} style={{ marginTop: '20px', marginLeft: '5px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          className="maintenanceCheckbox maintenanceTableCheckbox"
                          checked={showOnlyActive}
                          onChange={() => {
                                setShowOnlyActive(prev => !prev);
                                if (!showOnlyActive) setShowOnlyDestroyed(false);
                              }}
                              style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                      }
                      label="Show only active plants"
                    />
                    {isAdmin && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          className="maintenanceCheckbox maintenanceTableCheckbox"
                          checked={showOnlyDestroyed}
                          onChange={() => {
                                setShowOnlyDestroyed(prev => !prev);
                                if (!showOnlyDestroyed) setShowOnlyActive(false);
                              }}
                              style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                      }
                      label="Show only destroyed plants"
                    />
                    )}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="plot-select-label">Plot</InputLabel>
          <Select
            labelId="plot-select-label"
            value={selectedPlotNo}
            onChange={(e) => setSelectedPlotNo(e.target.value)}
            label="Plot"
          >
            <MenuItem value="">
              <em>All Plots</em>
            </MenuItem>
            {plots.filter(plot => plot.plotno !== "1").map((plot) => (
              <MenuItem key={plot.plot_id} value={plot.org_plot_no}>
                {plot.plotno || `Plot ${plot.org_plot_no}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        </FormGroup>
      </div>

      {/* Maintenance Type Checkboxes - always visible */}
      <div style={{ marginLeft: "20px", marginRight: "20px", marginBottom: "1rem", marginTop: "1.5rem" }}>
        <p className="dropdownTitle">
          Select Maintenance Data to Display
        </p>
        <FormGroup row style={{ marginTop: '20px', marginLeft: '5px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.phenotyping}
                onChange={() => handleMaintenanceTypeChange('phenotyping')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Phenotyping"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.measurement}
                onChange={() => handleMaintenanceTypeChange('measurement')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Measurements"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.harvest}
                onChange={() => handleMaintenanceTypeChange('harvest')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Harvest"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.postharvest}
                onChange={() => handleMaintenanceTypeChange('postharvest')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Post-harvest"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.irrigation}
                onChange={() => handleMaintenanceTypeChange('irrigation')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Irrigation"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.scouting}
                onChange={() => handleMaintenanceTypeChange('scouting')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Scouting"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.application}
                onChange={() => handleMaintenanceTypeChange('application')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Applications"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.manipulation}
                onChange={() => handleMaintenanceTypeChange('manipulation')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Manipulations"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={maintenanceTypes.status}
                onChange={() => handleMaintenanceTypeChange('status')}
                className="maintenanceCheckbox maintenanceTableCheckbox"
              />
            }
            label="Status Info"
          />
        </FormGroup>
      </div>

      <div className="plantTableDiv">
        <MaterialReactTable
          columns={columns}
          data={filteredPlants}
          enableRowSelection
          enableColumnOrdering
          enableHiding
          initialState={{
            showColumnFilters: true,
            pagination: { pageIndex: 0, pageSize: 100 },
            sorting: [{ id: 'org_plant_no', desc: true }]
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}
            >
              <Button
                color="primary"
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                variant="contained"
              >
                Export All Data
              </Button>
              <Button
                disabled={
                  !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                }
                onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                startIcon={<FileDownloadIcon />}
                variant="contained"
              >
                Export Selected Rows
              </Button>
              
              <Button
                disabled={
                  !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                }
                onClick={(e) => handleDeletePlants(e, table.getSelectedRowModel().rows)}
                startIcon={<DeleteIcon />}
                variant="contained"
              >
                Destroy Selected Plants
              </Button>
            </Box>
          )}
        />
      </div>
    </div>
  );
}

export default requireAuth(PlantSelect);
